import React, { useEffect, useState } from "react";

import { AxiosError } from "axios";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  Check,
  Close,
  Delete,
  DeleteOutline,
  FilterList,
  MoreVert,
  RestartAlt,
  Search,
  SubdirectoryArrowRight,
} from "@mui/icons-material";

import { Analysis } from "../../../../../app/models/general/analysis";
import { AnalysisPriority } from "../../../../../app/models/general/analysis-priority";
import {
  BomAdditionRelation,
  BomComponentAnalysisRow,
} from "../../../../../app/models/general/bom-component-analysis";
import { BomSubstitution } from "../../../../../app/models/general/bom-substitution";
import { PaginatedResult } from "@/app/models/default/paginated-result";

import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";

import { ErrorComponent } from "../../../../components/error/error.component";
import TablePaginationComponent from "../../../../components/table/table-pagination.component";
import BomTypeCell from "../../../../components/table/bom-type-cell.component";
import Loading from "../../../../components/loading/loading";

import { translate } from "../../../../translate/translate";

interface EditForm {
  substitute_part_number: string;
  start_date: string;
  end_date: string;
  substitute_description: string;
  substitute_unit: string;
  substitute_quantity: string;
}

const emptyEditForm: EditForm = {
  substitute_part_number: "",
  start_date: "",
  end_date: "",
  substitute_description: "",
  substitute_unit: "",
  substitute_quantity: "",
};

// Mirrors the max_length of the substitute_* CharFields on BomComponentSubstitution (core/models.py).
const SUBSTITUTE_TEXT_MAX_LENGTH = 256;

// Inline form for a brand-new PN manually added to the BOM within the simulation.
interface AdditionForm {
  item: string;
  part_number: string;
  description: string;
  quantity: string;
  unit: string;
  mtype: string;
  purchase_priority: string;
  consumption_priority: string;
  is_phantom: boolean;
  start_date: string;
  end_date: string;
}

const emptyAdditionForm: AdditionForm = {
  item: "",
  part_number: "",
  description: "",
  quantity: "",
  unit: "",
  mtype: "ZROH",
  purchase_priority: "1",
  consumption_priority: "",
  is_phantom: false,
  start_date: "",
  end_date: "",
};

// Material types and purchase priorities available in the add/edit form (mirror the
// backend BomComponent.Mtype / PurchasePriority choices).
const MTYPE_OPTIONS = ["ZHLB", "ZMOD", "ZROH"] as const;

// Describes which anchor row / relation a pending inline add is attached to.
interface PendingAddition {
  anchor: BomComponentAnalysisRow;
  relation: BomAdditionRelation;
}

interface ColumnDef {
  key: string;
  labelKey: string;
  required?: boolean;
}

// Columns the "Filtrar" button toggles visibility for. Required columns (structural,
// or needed for the substitution flow) are always shown.
const ALL_COLUMNS: ColumnDef[] = [
  { key: "level", labelKey: "level", required: true },
  { key: "item", labelKey: "item", required: true },
  { key: "part_number", labelKey: "part_number", required: true },
  { key: "substitution", labelKey: "substitute", required: true },
  { key: "description", labelKey: "description" },
  { key: "type", labelKey: "type" },
  { key: "quantity", labelKey: "quantity" },
  { key: "mtype", labelKey: "matl_type" },
  { key: "purchase_priority", labelKey: "purchase_priority" },
  { key: "consumption_priority", labelKey: "usage_priority" },
  { key: "unit", labelKey: "unit" },
  { key: "action", labelKey: "action", required: true },
];

const toDateInputValue = (isoDate?: string | null): string => (isoDate ? isoDate.slice(0, 10) : "");

// Formats a `YYYY-MM-DD`-prefixed ISO string using local date components, avoiding the
// UTC->local day shift that `new Date(iso).toLocaleDateString()` introduces for users in
// timezones behind UTC (e.g. America/Manaus, UTC-4).
const formatDateOnly = (isoDate?: string | null): string => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(translate.language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const isQuantityValid = (value: string): boolean => value !== "" && Number(value) > 0;

const isDateRangeValid = (start: string, end: string): boolean => !start || !end || start <= end;

// Substitute quantities must always be whole numbers; truncates any decimals.
const toIntegerString = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === "") return "";
  const truncated = Math.trunc(Number(value));
  return Number.isNaN(truncated) ? "" : String(truncated);
};

const SimulationBomComponent = ({ analysis, dataPriority }: { analysis: Analysis; dataPriority: AnalysisPriority[] }) => {
  const analysisService = new BaseService(URLS.ANALYSIS);

  const [selectedBomNumber, setSelectedBomNumber] = useState("");
  const [rows, setRows] = useState<BomComponentAnalysisRow[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>(emptyEditForm);

  // Draft values bound to the inputs; only copied into the applied (*Filter/searchText)
  // state — which actually triggers the search effect — via the magnifier button or Enter.
  const [levelInput, setLevelInput] = useState("");
  const [itemInput, setItemInput] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [levelFilter, setLevelFilter] = useState("");
  const [itemFilter, setItemFilter] = useState("");
  const [searchText, setSearchText] = useState("");

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<HTMLElement | null>(null);

  const [bomsWithSubstitutes, setBomsWithSubstitutes] = useState<string[]>([]);
  const [bomsWithAdditions, setBomsWithAdditions] = useState<string[]>([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Per-row context menu (Add above/below/subitem, Deactivate, Delete).
  const [rowMenuAnchor, setRowMenuAnchor] = useState<HTMLElement | null>(null);
  const [rowMenuRow, setRowMenuRow] = useState<BomComponentAnalysisRow | null>(null);

  // Inline "add new PN" form state (attached to an anchor row + relation).
  const [pendingAddition, setPendingAddition] = useState<PendingAddition | null>(null);
  const [additionForm, setAdditionForm] = useState<AdditionForm>(emptyAdditionForm);
  const [deleteAdditionRow, setDeleteAdditionRow] = useState<BomComponentAnalysisRow | null>(null);
  // Non-null while an already-added row is being edited in place (double-click).
  const [editingAdditionId, setEditingAdditionId] = useState<number | null>(null);

  const visibleColumnDefs = ALL_COLUMNS.filter((col) => col.required || !hiddenColumns.includes(col.key));
  const displayedColumns = visibleColumnDefs.map((col) => col.key);
  const displayedColumnsShow = visibleColumnDefs.map((col) => col.labelKey);

  const boms = Array.from(new Map(dataPriority.map((item) => [item.bom_number, item])).values());

  const search = () => {
    if (!selectedBomNumber) {
      setRows([]);
      setPagination(undefined);
      return;
    }

    analysisService.clearParameters();
    analysisService.addParameter("bom_number", selectedBomNumber);
    analysisService.addParameter("limit", rowsPerPage);
    analysisService.addParameter("offset", page * rowsPerPage);
    if (levelFilter) analysisService.addParameter("level", levelFilter);
    if (itemFilter) analysisService.addParameter("item", itemFilter);
    if (searchText) analysisService.addParameter("text", searchText);

    setLoading(true);
    analysisService
      .getFromDetailRoutePaginate(analysis.id, "bom-components")
      .then((response: any) => {
        setPagination(response.data);
        setRows(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    search();
  }, [selectedBomNumber, page, rowsPerPage, levelFilter, itemFilter, searchText]);

  const fetchBomsWithSubstitutes = () => {
    analysisService.clearParameters();
    analysisService
      .getFromDetailRoute(analysis.id, "boms-with-substitutions")
      .then((response: any) => {
        setBomsWithSubstitutes(response.data.bom_numbers ?? []);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      });
  };

  const fetchBomsWithAdditions = () => {
    analysisService.clearParameters();
    analysisService
      .getFromDetailRoute(analysis.id, "boms-with-additions")
      .then((response: any) => {
        setBomsWithAdditions(response.data.bom_numbers ?? []);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      });
  };

  useEffect(() => {
    fetchBomsWithSubstitutes();
    fetchBomsWithAdditions();
  }, []);

  const handleApplySearch = () => {
    setPage(0);
    setLevelFilter(levelInput);
    setItemFilter(itemInput);
    setSearchText(searchInput);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleApplySearch();
    }
  };

  const handleSelectBom = (event: any) => {
    setSelectedBomNumber(event.target.value);
    setPage(0);
    setEditingRowId(null);
    setLevelInput("");
    setItemInput("");
    setSearchInput("");
    setLevelFilter("");
    setItemFilter("");
    setSearchText("");
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleColumn = (key: string) => {
    setHiddenColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleStartAddSubstitute = (row: BomComponentAnalysisRow) => {
    setEditingRowId(row.bom_component_id);
    setEditForm({
      ...emptyEditForm,
      substitute_unit: row.unit ?? "",
      substitute_quantity: toIntegerString(row.quantity),
    });
  };

  const handleStartEditSubstitute = (row: BomComponentAnalysisRow) => {
    if (!row.substitution) return;
    setEditingRowId(row.bom_component_id);
    setEditForm({
      substitute_part_number: row.substitution.substitute_part_number ?? "",
      start_date: toDateInputValue(row.substitution.start_date),
      end_date: toDateInputValue(row.substitution.end_date),
      substitute_description: row.substitution.substitute_description ?? "",
      substitute_unit: row.substitution.substitute_unit ?? "",
      substitute_quantity: toIntegerString(row.substitution.substitute_quantity),
    });
  };

  const handleCancelAddSubstitute = () => {
    setEditingRowId(null);
    setEditForm(emptyEditForm);
  };

  const handleConfirmAddSubstitute = (row: BomComponentAnalysisRow) => {
    if (!editForm.substitute_part_number || !editForm.start_date) {
      toast.error(translate.t("invalid_substitution_data"));
      return;
    }

    if (!isDateRangeValid(editForm.start_date, editForm.end_date)) {
      toast.error(translate.t("must_be_bigger_equal_than_start_date"));
      return;
    }

    if (!isQuantityValid(editForm.substitute_quantity)) {
      toast.error(translate.t("substitute_quantity_must_be_positive"));
      return;
    }

    const payload = {
      bom_component_id: row.bom_component_id,
      substitute_part_number: editForm.substitute_part_number.toUpperCase(),
      substitute_description: editForm.substitute_description.toUpperCase() || null,
      substitute_unit: editForm.substitute_unit,
      substitute_quantity: toIntegerString(editForm.substitute_quantity),
      // Sent at UTC noon (not midnight) so the date doesn't shift back a day once the
      // backend round-trips it through its timezone (America/Manaus, UTC-4).
      start_date: `${editForm.start_date}T12:00:00Z`,
      end_date: editForm.end_date ? `${editForm.end_date}T12:00:00Z` : null,
    };

    const substitutionId = row.substitution?.id;
    const isEditing = substitutionId != null;

    analysisService.clearParameters();
    const request = isEditing
      ? (() => {
          analysisService.addParameter("substitution_id", substitutionId);
          return analysisService.patchFromDetailRoute(payload, analysis.id, "update-substitution");
        })()
      : analysisService.saveFromDetailRoute(payload, analysis.id, "add-substitution");

    request
      .then((response: any) => {
        setRows((prev) =>
          prev.map((r) =>
            r.bom_component_id === row.bom_component_id ? { ...r, substitution: response.data } : r,
          ),
        );
        setEditingRowId(null);
        setEditForm(emptyEditForm);
        if (!isEditing) fetchBomsWithSubstitutes();
        toast.success(
          translate.t(isEditing ? "successfully_updated_substitute" : "successfully_added_substitute"),
        );
      })
      .catch((error: AxiosError) => {
        const status = error.response?.status;
        if (status === 409) {
          toast.error(translate.t("substitution_already_exists"));
        } else if (status === 400) {
          toast.error(translate.t("invalid_substitution_data"));
        } else {
          ErrorComponent(error);
        }
      });
  };

  const handleDeleteSubstitute = (row: BomComponentAnalysisRow) => {
    if (!row.substitution) return;

    analysisService.clearParameters();
    analysisService.addParameter("substitution_id", row.substitution.id);
    analysisService
      .deleteFromDetailRoute(analysis.id, "delete-substitution")
      .then(() => {
        setRows((prev) =>
          prev.map((r) =>
            r.bom_component_id === row.bom_component_id ? { ...r, substitution: null } : r,
          ),
        );
        fetchBomsWithSubstitutes();
        toast.success(translate.t("successfully_deleted"));
      })
      .catch((error: AxiosError) => {
        ErrorComponent(error);
      });
  };

  const handleResetSimulation = () => {
    if (!selectedBomNumber) return;

    // Reset clears both the substitutions and the manually-added PNs of this BOM.
    analysisService.clearParameters();
    analysisService.addParameter("bom_number", selectedBomNumber);
    analysisService
      .deleteFromDetailRoute(analysis.id, "reset-substitutions")
      .then(() => {
        analysisService.clearParameters();
        analysisService.addParameter("bom_number", selectedBomNumber);
        return analysisService.deleteFromDetailRoute(analysis.id, "reset-bom-additions");
      })
      .then(() => {
        // Drop the added rows and clear substitutions from the remaining original rows.
        setRows((prev) => prev.filter((r) => !r.is_manual_addition).map((r) => ({ ...r, substitution: null })));
        setEditingRowId(null);
        setEditForm(emptyEditForm);
        cancelAddition();
        fetchBomsWithSubstitutes();
        fetchBomsWithAdditions();
        setShowResetConfirm(false);
        toast.success(translate.t("successfully_reset_simulation"));
      })
      .catch((error: AxiosError) => {
        ErrorComponent(error);
      });
  };

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, row: BomComponentAnalysisRow) => {
    setRowMenuAnchor(event.currentTarget);
    setRowMenuRow(row);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuRow(null);
  };

  const startAddition = (row: BomComponentAnalysisRow, relation: BomAdditionRelation) => {
    // Cancel any in-flight substitution edit so both inline editors never overlap.
    setEditingRowId(null);
    setEditForm(emptyEditForm);
    setPendingAddition({ anchor: row, relation });
    setAdditionForm({
      ...emptyAdditionForm,
      item: row.item != null ? String(row.item) : "",
      unit: row.unit ?? "",
    });
    closeRowMenu();
  };

  const startEditAddition = (row: BomComponentAnalysisRow) => {
    // Cancel any in-flight substitution edit / pending create so editors never overlap.
    setEditingRowId(null);
    setEditForm(emptyEditForm);
    setPendingAddition(null);
    setAdditionForm({
      item: row.item != null ? String(row.item) : "",
      part_number: row.part_number ?? "",
      description: row.description ?? "",
      quantity: toIntegerString(row.quantity),
      unit: row.unit ?? "",
      mtype: row.mtype ?? "ZROH",
      purchase_priority: row.purchase_priority != null ? String(row.purchase_priority) : "1",
      consumption_priority: row.consumption_priority != null ? String(row.consumption_priority) : "",
      is_phantom: !!row.is_phantom,
      start_date: toDateInputValue(row.start_date),
      end_date: toDateInputValue(row.end_date),
    });
    setEditingAdditionId(row.addition_id ?? null);
  };

  const handleDoubleClickRow = (row: BomComponentAnalysisRow) => {
    if (row.is_manual_addition && row.addition_id) {
      startEditAddition(row);
    }
  };

  const cancelAddition = () => {
    setPendingAddition(null);
    setEditingAdditionId(null);
    setAdditionForm(emptyAdditionForm);
  };

  const canConfirmAddition =
    !!additionForm.part_number.trim() &&
    isQuantityValid(additionForm.quantity) &&
    !!additionForm.start_date &&
    isDateRangeValid(additionForm.start_date, additionForm.end_date);

  const handleConfirmAddition = () => {
    const isEditing = editingAdditionId != null;
    if (!pendingAddition && !isEditing) return;

    if (!additionForm.part_number.trim()) {
      toast.error(translate.t("part_number_required"));
      return;
    }

    if (!isQuantityValid(additionForm.quantity)) {
      toast.error(translate.t("substitute_quantity_must_be_positive"));
      return;
    }

    if (!additionForm.start_date) {
      toast.error(translate.t("invalid_addition_data"));
      return;
    }

    if (!isDateRangeValid(additionForm.start_date, additionForm.end_date)) {
      toast.error(translate.t("must_be_bigger_equal_than_start_date"));
      return;
    }

    const payload: Record<string, unknown> = {
      bom_number: selectedBomNumber,
      item: additionForm.item !== "" ? Number(additionForm.item) : null,
      part_number: additionForm.part_number.trim().toUpperCase(),
      description: additionForm.description.toUpperCase() || null,
      unit: additionForm.unit || null,
      quantity: toIntegerString(additionForm.quantity),
      mtype: additionForm.mtype,
      purchase_priority: Number(additionForm.purchase_priority),
      consumption_priority: additionForm.consumption_priority !== "" ? Number(additionForm.consumption_priority) : null,
      is_phantom: additionForm.is_phantom,
      // Sent at UTC noon (not midnight) so the date doesn't shift back a day once the
      // backend round-trips it through its timezone (America/Manaus, UTC-4).
      start_date: `${additionForm.start_date}T12:00:00Z`,
      end_date: additionForm.end_date ? `${additionForm.end_date}T12:00:00Z` : null,
    };

    if (isEditing) {
      analysisService.clearParameters();
      analysisService.addParameter("addition_id", editingAdditionId);
      analysisService
        .patchFromDetailRoute(payload, analysis.id, "update-bom-component")
        .then(() => {
          cancelAddition();
          toast.success(translate.t("successfully_updated_pn"));
          search();
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 400) {
            const backendError = (error.response?.data as { error?: string } | undefined)?.error;
            toast.error(backendError ?? translate.t("invalid_addition_data"));
          } else {
            ErrorComponent(error);
          }
        });
      return;
    }

    if (!pendingAddition) return;

    const { anchor, relation } = pendingAddition;
    const isAnchorAddition = !!anchor.is_manual_addition;
    payload.relation = relation;

    // Anchor an addition to an original BomComponent, or (for a subitem of an added row)
    // to another addition via parent_addition_id.
    if (isAnchorAddition) {
      payload.parent_addition_id = anchor.addition_id;
    } else {
      payload.anchor_component_id = anchor.bom_component_id;
    }

    analysisService.clearParameters();
    analysisService
      .saveFromDetailRoute(payload, analysis.id, "add-bom-component")
      .then(() => {
        cancelAddition();
        toast.success(translate.t("successfully_added_pn"));
        search();
        fetchBomsWithAdditions();
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === 400) {
          const backendError = (error.response?.data as { error?: string } | undefined)?.error;
          toast.error(backendError ?? translate.t("invalid_addition_data"));
        } else {
          ErrorComponent(error);
        }
      });
  };

  const handleDeleteAddition = () => {
    if (!deleteAdditionRow?.addition_id) return;

    analysisService.clearParameters();
    analysisService.addParameter("addition_id", deleteAdditionRow.addition_id);
    analysisService
      .deleteFromDetailRoute(analysis.id, "delete-bom-component")
      .then(() => {
        setDeleteAdditionRow(null);
        toast.success(translate.t("successfully_deleted_pn"));
        search();
        fetchBomsWithAdditions();
      })
      .catch((error: AxiosError) => {
        ErrorComponent(error);
      });
  };

  const renderSubstitutionCell = (row: BomComponentAnalysisRow) => {
    if (row.substitution) {
      return (
        <Typography variant="caption" component="span" sx={{
          fontFamily: "monospace",
          fontWeight: 600,
          color: "#3949AB",
          backgroundColor: "rgba(57, 73, 171, 0.08)",
          border: "1px solid rgba(57, 73, 171, 0.25)",
          borderRadius: "4px",
          padding: "2px 6px",
        }}>
          → {row.substitution.substitute_part_number}
        </Typography>
      );
    }

    if (!row.can_substitute) {
      return null;
    }

    if (editingRowId === row.bom_component_id) {
      return null;
    }

    return (
      <Button size="small" variant="text" onClick={() => handleStartAddSubstitute(row)}>
        {translate.t("add_substitute")}
      </Button>
    );
  };

  const canConfirmSubstitute =
    !!editForm.substitute_part_number &&
    !!editForm.start_date &&
    isDateRangeValid(editForm.start_date, editForm.end_date) &&
    isQuantityValid(editForm.substitute_quantity);

  const placeholderCell = (
    <Typography variant="body2" color="text.disabled">—</Typography>
  );

  const connectorCell = (
    <Typography variant="body2" sx={{ color: "rgba(57, 73, 171, 0.5)", fontFamily: "monospace" }}>
      └
    </Typography>
  );

  const renderEditCell = (column: string, row: BomComponentAnalysisRow) => {
    switch (column) {
      case "level":
        return connectorCell;
      case "item":
        return (
          <Typography variant="body2" sx={{ color: "#3949AB", fontWeight: 600 }}>
            {row.item}
          </Typography>
        );
      case "part_number":
        return (
          <TextField
            autoFocus
            size="small"
            placeholder={translate.t("new_part_number")}
            inputProps={{ "aria-label": translate.t("new_part_number"), maxLength: SUBSTITUTE_TEXT_MAX_LENGTH }}
            value={editForm.substitute_part_number}
            onChange={(e) => setEditForm((prev) => ({ ...prev, substitute_part_number: e.target.value }))}
            fullWidth
          />
        );
      case "substitution": {
        const endDateInvalid = !!editForm.end_date && !isDateRangeValid(editForm.start_date, editForm.end_date);
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <TextField
              size="small"
              type="date"
              inputProps={{ "aria-label": translate.t("start_date") }}
              InputLabelProps={{ shrink: true }}
              value={editForm.start_date}
              onChange={(e) => setEditForm((prev) => ({ ...prev, start_date: e.target.value }))}
              error={!editForm.start_date}
              sx={{ width: "130px" }}
            />
            <Typography variant="body2" color="text.secondary">→</Typography>
            <TextField
              size="small"
              type="date"
              inputProps={{ "aria-label": translate.t("end_date") }}
              InputLabelProps={{ shrink: true }}
              value={editForm.end_date}
              onChange={(e) => setEditForm((prev) => ({ ...prev, end_date: e.target.value }))}
              error={endDateInvalid}
              helperText={endDateInvalid ? translate.t("must_be_bigger_equal_than_start_date") : undefined}
              sx={{ width: "130px" }}
            />
          </Box>
        );
      }
      case "description":
        return (
          <TextField
            size="small"
            placeholder={translate.t("description")}
            inputProps={{ "aria-label": translate.t("description"), maxLength: SUBSTITUTE_TEXT_MAX_LENGTH }}
            value={editForm.substitute_description}
            onChange={(e) => setEditForm((prev) => ({ ...prev, substitute_description: e.target.value }))}
            fullWidth
          />
        );
      case "quantity": {
        const quantityInvalid = !isQuantityValid(editForm.substitute_quantity);
        return (
          <TextField
            size="small"
            type="number"
            placeholder={toIntegerString(row.quantity)}
            inputProps={{ "aria-label": translate.t("quantity"), min: 1, step: 1 }}
            value={editForm.substitute_quantity}
            onChange={(e) => setEditForm((prev) => ({ ...prev, substitute_quantity: toIntegerString(e.target.value) }))}
            error={quantityInvalid}
            sx={{ width: "80px" }}
          />
        );
      }
      case "unit":
        return (
          <TextField
            size="small"
            placeholder={translate.t("unit")}
            inputProps={{ "aria-label": translate.t("unit"), maxLength: SUBSTITUTE_TEXT_MAX_LENGTH }}
            value={editForm.substitute_unit}
            onChange={(e) => setEditForm((prev) => ({ ...prev, substitute_unit: e.target.value }))}
            sx={{ width: "72px" }}
          />
        );
      case "action":
        return (
          <Box display="flex" gap={0.5} justifyContent="flex-end">
            <IconButton
              size="small"
              color="success"
              disabled={!canConfirmSubstitute}
              onClick={() => handleConfirmAddSubstitute(row)}
              aria-label={translate.t("confirm")}
            >
              <Check fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={handleCancelAddSubstitute}
              aria-label={translate.t("cancel")}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        );
      default:
        return placeholderCell;
    }
  };

  const renderDisplayCell = (column: string, row: BomComponentAnalysisRow, substitution: BomSubstitution) => {
    switch (column) {
      case "level":
        return connectorCell;
      case "item":
        return (
          <Typography variant="body2" sx={{ color: "#6D28D9", fontWeight: 600 }}>
            {row.item}
          </Typography>
        );
      case "part_number":
        return (
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600, color: "#6D28D9" }}>
            {substitution.substitute_part_number}
          </Typography>
        );
      case "substitution": {
        const dateRangeLabel = substitution.end_date
          ? `${formatDateOnly(substitution.start_date)} - ${formatDateOnly(substitution.end_date)}`
          : `${translate.t("starting_from")} ${formatDateOnly(substitution.start_date)}`;
        return (
          <Typography variant="caption" sx={{ color: "#6D28D9", whiteSpace: "nowrap" }}>
            {dateRangeLabel}
          </Typography>
        );
      }
      case "description":
        // Replicates the original component's description when the substitute didn't change it.
        return (
          <Typography variant="body2" sx={{ color: "#6D28D9" }}>
            {substitution.substitute_description || row.description}
          </Typography>
        );
      case "quantity":
        // Replicates the original component's quantity when the substitute didn't change it.
        return (
          <Typography variant="body2" sx={{ color: "#6D28D9", fontWeight: 600 }}>
            {toIntegerString(substitution.substitute_quantity) || toIntegerString(row.quantity)}
          </Typography>
        );
      case "unit":
        // Replicates the original component's unit when the substitute didn't change it.
        return (
          <Typography variant="body2" sx={{ color: "#6D28D9", fontWeight: 600 }}>
            {substitution.substitute_unit || row.unit}
          </Typography>
        );
      case "action":
        return (
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteSubstitute(row)}
            aria-label={translate.t("delete")}
          >
            <Delete fontSize="small" />
          </IconButton>
        );
      default:
        // Columns the substitution doesn't carry its own value for (type, mtype,
        // priorities, ...) replicate the original component's row as-is.
        return renderCellHandler(column, 0, row);
    }
  };

  // Stable key for a row regardless of whether it is an original component (bom_component_id)
  // or a manually-added overlay row (addition_id).
  const rowKey = (row: BomComponentAnalysisRow) =>
    row.is_manual_addition ? `a${row.addition_id}` : `c${row.bom_component_id}`;

  const isAdditionAnchor = (row: BomComponentAnalysisRow) =>
    !!pendingAddition && rowKey(pendingAddition.anchor) === rowKey(row);

  const additionCellSx = {
    fontSize: "0.75rem",
    "& .MuiTypography-root": { fontSize: "0.75rem" },
  };

  const renderAdditionEditCell = (column: string, quantityInvalid: boolean) => {
    const endDateInvalid =
      !!additionForm.end_date && !isDateRangeValid(additionForm.start_date, additionForm.end_date);
    switch (column) {
      case "level":
        return connectorCell;
      case "substitution":
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <TextField
              size="small"
              type="date"
              inputProps={{ "aria-label": translate.t("start_date") }}
              InputLabelProps={{ shrink: true }}
              value={additionForm.start_date}
              onChange={(e) => setAdditionForm((prev) => ({ ...prev, start_date: e.target.value }))}
              error={!additionForm.start_date}
              sx={{ width: "130px" }}
            />
            <Typography variant="body2" color="text.secondary">
              →
            </Typography>
            <TextField
              size="small"
              type="date"
              inputProps={{ "aria-label": translate.t("end_date") }}
              InputLabelProps={{ shrink: true }}
              value={additionForm.end_date}
              onChange={(e) => setAdditionForm((prev) => ({ ...prev, end_date: e.target.value }))}
              error={endDateInvalid}
              helperText={endDateInvalid ? translate.t("must_be_bigger_equal_than_start_date") : undefined}
              sx={{ width: "130px" }}
            />
          </Box>
        );
      case "item":
        return (
          <TextField
            size="small"
            type="number"
            placeholder={translate.t("item")}
            inputProps={{ "aria-label": translate.t("item"), min: 0, step: 1 }}
            value={additionForm.item}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, item: toIntegerString(e.target.value) }))}
            sx={{ width: "80px" }}
          />
        );
      case "part_number":
        return (
          <TextField
            autoFocus
            size="small"
            placeholder={translate.t("new_pn_part_number")}
            inputProps={{ "aria-label": translate.t("new_pn_part_number"), maxLength: SUBSTITUTE_TEXT_MAX_LENGTH }}
            value={additionForm.part_number}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, part_number: e.target.value }))}
            error={!additionForm.part_number.trim()}
            fullWidth
          />
        );
      case "description":
        return (
          <TextField
            size="small"
            placeholder={translate.t("description")}
            inputProps={{ "aria-label": translate.t("description"), maxLength: SUBSTITUTE_TEXT_MAX_LENGTH }}
            value={additionForm.description}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
          />
        );
      case "quantity":
        return (
          <TextField
            size="small"
            type="number"
            inputProps={{ "aria-label": translate.t("quantity"), min: 1, step: 1 }}
            value={additionForm.quantity}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, quantity: toIntegerString(e.target.value) }))}
            error={quantityInvalid}
            sx={{ width: "80px" }}
          />
        );
      case "unit":
        return (
          <TextField
            size="small"
            placeholder={translate.t("unit")}
            inputProps={{ "aria-label": translate.t("unit"), maxLength: SUBSTITUTE_TEXT_MAX_LENGTH }}
            value={additionForm.unit}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, unit: e.target.value }))}
            sx={{ width: "72px" }}
          />
        );
      case "type":
        return (
          <Checkbox
            size="small"
            checked={additionForm.is_phantom}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, is_phantom: e.target.checked }))}
            inputProps={{ "aria-label": translate.t("phantom") }}
          />
        );
      case "mtype":
        return (
          <Select
            size="small"
            value={additionForm.mtype}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, mtype: e.target.value }))}
            inputProps={{ "aria-label": translate.t("matl_type") }}
            sx={{ width: "96px" }}
          >
            {MTYPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      case "purchase_priority":
        return (
          <Select
            size="small"
            value={additionForm.purchase_priority}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, purchase_priority: e.target.value }))}
            inputProps={{ "aria-label": translate.t("purchase_priority") }}
            sx={{ width: "120px" }}
          >
            <MenuItem value="1">{translate.t("prime")}</MenuItem>
            <MenuItem value="2">{translate.t("alternative")}</MenuItem>
          </Select>
        );
      case "consumption_priority":
        return (
          <TextField
            size="small"
            type="number"
            placeholder={translate.t("usage_priority")}
            inputProps={{ "aria-label": translate.t("usage_priority"), min: 0, step: 1 }}
            value={additionForm.consumption_priority}
            onChange={(e) => setAdditionForm((prev) => ({ ...prev, consumption_priority: toIntegerString(e.target.value) }))}
            sx={{ width: "80px" }}
          />
        );
      case "action":
        return (
          <Box display="flex" gap={0.5} justifyContent="flex-end">
            <IconButton
              size="small"
              color="success"
              disabled={!canConfirmAddition}
              onClick={handleConfirmAddition}
              aria-label={translate.t("confirm")}
            >
              <Check fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={cancelAddition} aria-label={translate.t("cancel")}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        );
      default:
        return placeholderCell;
    }
  };

  const renderAdditionFormRow = (row: BomComponentAnalysisRow) => {
    const quantityInvalid = !isQuantityValid(additionForm.quantity);
    return (
      <TableRow
        key={`addition-form-${rowKey(row)}`}
        sx={{
          backgroundColor: "rgba(236, 253, 245, 0.7) !important",
          borderLeft: "3px solid #6EE7B7",
        }}
      >
        {displayedColumns.map((column) => (
          <TableCell key={column} align={column === "action" ? "right" : undefined} sx={additionCellSx}>
            {renderAdditionEditCell(column, quantityInvalid)}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const renderSubstituteRow = (row: BomComponentAnalysisRow) => {
    const additionFormRow = isAdditionAnchor(row) ? renderAdditionFormRow(row) : null;
    const isEditing = row.bom_component_id !== null && editingRowId === row.bom_component_id;

    if (!isEditing && !row.substitution) {
      return additionFormRow;
    }

    const rowSx = {
      backgroundColor: "rgba(245, 243, 255, 0.6) !important",
      borderLeft: "3px solid #C4B5FD",
    };

    const cellSx = {
      fontSize: "0.75rem",
      "& .MuiTypography-root": { fontSize: "0.75rem" },
    };

    if (isEditing) {
      return (
        <>
          <TableRow key={`substitute-form-${row.bom_component_id}`} sx={rowSx}>
            {displayedColumns.map((column) => (
              <TableCell key={column} align={column === "action" ? "right" : undefined} sx={cellSx}>
                {renderEditCell(column, row)}
              </TableCell>
            ))}
          </TableRow>
          {additionFormRow}
        </>
      );
    }

    const substitution = row.substitution;
    if (!substitution) {
      return additionFormRow;
    }

    return (
      <>
        <TableRow
          key={`substitute-${row.bom_component_id}`}
          sx={{ ...rowSx, cursor: "pointer" }}
          onDoubleClick={() => handleStartEditSubstitute(row)}
        >
          {displayedColumns.map((column) => (
            <TableCell key={column} align={column === "action" ? "right" : undefined} sx={cellSx}>
              {renderDisplayCell(column, row, substitution)}
            </TableCell>
          ))}
        </TableRow>
        {additionFormRow}
      </>
    );
  };

  const renderActionCell = (row: BomComponentAnalysisRow) => (
    <IconButton
      size="small"
      onClick={(e) => openRowMenu(e, row)}
      aria-label={translate.t("action")}
    >
      <MoreVert fontSize="small" />
    </IconButton>
  );

  const renderPartNumberCell = (row: BomComponentAnalysisRow) => {
    const inactive = row.is_manual_addition && row.active === false;
    return (
      <Box display="flex" alignItems="center" gap={0.75}>
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontFamily: "monospace",
            fontWeight: 600,
            color: inactive ? "text.disabled" : "#047857",
            textDecoration: inactive ? "line-through" : "none",
          }}
        >
          {row.part_number}
        </Typography>
        <Chip
          size="small"
          label={translate.t("manually_added")}
          sx={{
            height: 18,
            fontSize: "0.65rem",
            backgroundColor: "rgba(4, 120, 87, 0.1)",
            color: "#047857",
            border: "1px solid rgba(4, 120, 87, 0.25)",
          }}
        />
      </Box>
    );
  };

  const renderAdditionDateCell = (row: BomComponentAnalysisRow) => {
    if (!row.start_date) return null;
    const label = row.end_date
      ? `${formatDateOnly(row.start_date)} - ${formatDateOnly(row.end_date)}`
      : `${translate.t("starting_from")} ${formatDateOnly(row.start_date)}`;
    return (
      <Typography variant="caption" sx={{ color: "#047857", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    );
  };

  const renderCellHandler = (column: string, _index: number, row: BomComponentAnalysisRow) => {
    // A manually-added row being edited in place (double-click): every cell becomes an
    // input, reusing the add-form renderer (including the dates column and confirm/cancel).
    if (row.is_manual_addition && editingAdditionId != null && editingAdditionId === row.addition_id) {
      return renderAdditionEditCell(column, !isQuantityValid(additionForm.quantity));
    }
    if (column === "action") {
      // Original rows only offer "add" actions; added rows also offer deactivate/delete.
      return renderActionCell(row);
    }
    if (column === "part_number" && row.is_manual_addition) {
      return renderPartNumberCell(row);
    }
    if (column === "substitution") {
      if (row.is_manual_addition) {
        return renderAdditionDateCell(row);
      }
      return renderSubstitutionCell(row);
    }
    if (column === "type") {
      return <BomTypeCell is_phantom={row.is_phantom} is_imperfect={row.is_imperfect} />;
    }
    if (column === "purchase_priority") {
      const label = row.purchase_priority_label;
      if (label && label.trim().toLowerCase() === "not informed") {
        return translate.t("not_informed");
      }
      return label ?? row.purchase_priority;
    }
    if (column === "quantity") {
      return toIntegerString(row.quantity);
    }
    return (row as any)[column];
  };

  const renderCellHandlers: Record<string, typeof renderCellHandler> = {};
  displayedColumns.forEach((col) => {
    renderCellHandlers[col] = renderCellHandler;
  });

  return (
    <section>
      <Loading open={loading} />
      <Paper variant="outlined" sx={{ padding: "16px", marginBottom: "16px" }}>
        <Grid container spacing={2} alignItems="center" sx={{ marginBottom: "16px" }}>
          <Grid item>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="select-bom-model-label">{translate.t("bom_model")}</InputLabel>
              <Select
                labelId="select-bom-model-label"
                label={translate.t("bom_model")}
                value={selectedBomNumber}
                onChange={handleSelectBom}
              >
                {boms.map((item) => (
                  <MenuItem key={item.bom_number} value={item.bom_number}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      {item.part_number}
                      {(bomsWithSubstitutes.includes(item.bom_number ?? "") ||
                        bomsWithAdditions.includes(item.bom_number ?? "")) && (
                        <Box
                          component="span"
                          aria-label={translate.t("substitute")}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#3949AB",
                            display: "inline-block",
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedBomNumber && (
            <>
              <Grid item sx={{ width: 110 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={translate.t("level")}
                  value={levelInput}
                  onChange={(e) => setLevelInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </Grid>
              <Grid item sx={{ width: 110 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={translate.t("item")}
                  value={itemInput}
                  onChange={(e) => setItemInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </Grid>
              <Grid item sx={{ flexGrow: 1, minWidth: 240 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={translate.t("search_part_number_description_substitute")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </Grid>
              <Grid item>
                <Fab size="small" color="primary" onClick={handleApplySearch} aria-label={translate.t("search")}>
                  <Search />
                </Fab>
              </Grid>
              <Grid item>
                <Divider orientation="vertical" variant="fullWidth" sx={{ height: "40px" }} />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                >
                  {translate.t("filter")}
                </Button>
                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={!!columnsMenuAnchor}
                  onClose={() => setColumnsMenuAnchor(null)}
                >
                  {ALL_COLUMNS.filter((col) => !col.required).map((col) => (
                    <MenuItem key={col.key} dense onClick={() => toggleColumn(col.key)}>
                      <Checkbox size="small" checked={!hiddenColumns.includes(col.key)} />
                      <ListItemText primary={translate.t(col.labelKey)} />
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>
              {(bomsWithSubstitutes.includes(selectedBomNumber) ||
                bomsWithAdditions.includes(selectedBomNumber)) && (
                <Grid item>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestartAlt fontSize="small" />}
                    onClick={() => setShowResetConfirm(true)}
                  >
                    {translate.t("reset_simulation")}
                  </Button>
                </Grid>
              )}
            </>
          )}
        </Grid>

        {!selectedBomNumber ? (
          <Typography color="text.secondary">{translate.t("select_bom_to_view_components")}</Typography>
        ) : (
          <TablePaginationComponent
            state={rows}
            displayedColumnsShow={displayedColumnsShow}
            displayedColumns={displayedColumns}
            count={pagination?.count}
            next={pagination?.next}
            previous={pagination?.previous}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            renderCellHandler={renderCellHandlers}
            handleDoubleClick={handleDoubleClickRow}
            renderRowExtra={renderSubstituteRow}
            rowCustomStyleHandler={(row: BomComponentAnalysisRow) => {
              if (row.is_manual_addition) {
                return {
                  backgroundColor:
                    row.active === false
                      ? "rgba(0, 0, 0, 0.04) !important"
                      : "rgba(236, 253, 245, 0.7) !important",
                  borderLeft: "3px solid #10B981",
                  opacity: row.active === false ? 0.6 : 1,
                };
              }
              return row.substitution
                ? { backgroundColor: "rgba(255, 251, 235, 0.7) !important", borderLeft: "3px solid #FBBF24" }
                : {};
            }}
            actionHeaderLabel=""
            tableLayout="auto"
            stickyHeader
            disablePointer
            removeStripe
          />
        )}
      </Paper>

      <Menu anchorEl={rowMenuAnchor} open={!!rowMenuAnchor} onClose={closeRowMenu}>
        {rowMenuRow && !rowMenuRow.is_manual_addition && (
          <MenuItem onClick={() => startAddition(rowMenuRow, BomAdditionRelation.ABOVE)}>
            <ListItemIcon>
              <ArrowUpward fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={translate.t("add_above")} />
          </MenuItem>
        )}
        {rowMenuRow && !rowMenuRow.is_manual_addition && (
          <MenuItem onClick={() => startAddition(rowMenuRow, BomAdditionRelation.BELOW)}>
            <ListItemIcon>
              <ArrowDownward fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={translate.t("add_below")} />
          </MenuItem>
        )}
        {rowMenuRow && (
          <MenuItem onClick={() => startAddition(rowMenuRow, BomAdditionRelation.CHILD)}>
            <ListItemIcon>
              <SubdirectoryArrowRight fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={translate.t("add_subitem")} />
          </MenuItem>
        )}
        {rowMenuRow?.is_manual_addition && (
          <MenuItem
            onClick={() => {
              setDeleteAdditionRow(rowMenuRow);
              closeRowMenu();
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteOutline fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary={translate.t("exclude")} />
          </MenuItem>
        )}
      </Menu>

      <Dialog open={showResetConfirm} onClose={() => setShowResetConfirm(false)}>
        <DialogTitle>{translate.t("reset_simulation")}</DialogTitle>
        <DialogContent>
          <Typography>{translate.t("reset_simulation_confirm_message")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetConfirm(false)}>{translate.t("cancel")}</Button>
          <Button color="warning" variant="contained" onClick={handleResetSimulation}>
            {translate.t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteAdditionRow} onClose={() => setDeleteAdditionRow(null)}>
        <DialogTitle>{translate.t("delete_pn_confirm_title")}</DialogTitle>
        <DialogContent>
          <Typography>{translate.t("delete_pn_confirm_message")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAdditionRow(null)}>{translate.t("cancel")}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteAddition}>
            {translate.t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default SimulationBomComponent;
